import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { User } from '@/types'
import { authService } from '@/services/auth'
import { setAuthClearHandler } from '@/services/api'

// When an admin is viewing a customer dashboard read-only (a "view-as" tab),
// a per-tab token lives in sessionStorage. In that case the whole auth session
// is isolated to sessionStorage so it NEVER touches the admin's localStorage
// session in other tabs. Normal tabs (no flag) always use localStorage — i.e.
// unchanged behaviour for regular customers/admins.
export const isViewAsTab = (): boolean => {
  try { return !!sessionStorage.getItem('view_as_token') } catch { return false }
}
const sessionAwareStorage = {
  getItem: (name: string) => (isViewAsTab() ? sessionStorage : localStorage).getItem(name),
  setItem: (name: string, value: string) => (isViewAsTab() ? sessionStorage : localStorage).setItem(name, value),
  removeItem: (name: string) => (isViewAsTab() ? sessionStorage : localStorage).removeItem(name),
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  setUser: (user: User | null) => void
  login: (accessToken: string, refreshToken: string, user: User) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  checkAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      login: (accessToken, refreshToken, user) => {
        localStorage.setItem('access_token', accessToken)
        localStorage.setItem('refresh_token', refreshToken)
        set({ user, isAuthenticated: true, error: null })
      },

      logout: () => {
        if (isViewAsTab()) {
          // Read-only view-as tab: only tear down the per-tab session, never
          // touch the admin's localStorage session.
          sessionStorage.removeItem('view_as_token')
          sessionStorage.removeItem('view_as_name')
          sessionStorage.removeItem('auth-storage')
        } else {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('auth-storage')
        }
        set({ user: null, isAuthenticated: false, error: null })
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      checkAuth: async () => {
        const token = sessionStorage.getItem('view_as_token') || localStorage.getItem('access_token')
        if (!token) {
          set({ user: null, isAuthenticated: false })
          return
        }

        try {
          set({ isLoading: true })
          const user = await authService.getCurrentUser()
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error) {
          if (isViewAsTab()) {
            sessionStorage.removeItem('view_as_token')
            sessionStorage.removeItem('view_as_name')
          } else {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
          }
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionAwareStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // On rehydration, verify tokens still exist
        const token = sessionStorage.getItem('view_as_token') || localStorage.getItem('access_token')
        if (!token && state?.isAuthenticated) {
          // Token was cleared but state says authenticated - reset
          state.user = null
          state.isAuthenticated = false
        }
      },
    }
  )
)

// Register the auth clear handler for the API interceptor
setAuthClearHandler(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false, error: null })
})
